import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./Button";
import { cx, prefersReducedMotion } from "./util";

/** Top bar: brand mark (official logo image, or the mono "graspd_" mark with blinking mint cursor as a fallback), lowercase links, primary CTA. A link with `site: true` switches to a different site (bold, grouped at the end). Below 640px the links collapse into a [ menu ] dropdown. */
export default function Nav({ links = [], cta, ctaHref = "#", href = "/", suffix, logo, logoAlt = "Graspd", className, style }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // The highlighted link follows clicks on in-page links (#anchors, or the link to this very page).
  // Links that navigate to another route leave it alone, since the page is about to be replaced.
  const [active, setActive] = useState(() => links.findIndex((l) => typeof l !== "string" && l.current));
  const samePage = (h) => !!h && (h.charAt(0) === "#" || h === window.location.pathname);
  const pick = (e, i, h) => {
    setOpen(false);
    // Links to another route go through the router: a plain <a> reloads the whole app.
    if (!samePage(h) && h && h.charAt(0) === "/" && h.charAt(1) !== "/" && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button)) {
      e.preventDefault();
      navigate(h);
      window.scrollTo(0, 0);
      return;
    }
    if (!samePage(h)) return;
    setActive(i);
    if (h.charAt(0) !== "#") { e.preventDefault(); window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" }); }
  };
  const navRef = useRef(null), toggleRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); toggleRef.current && toggleRef.current.focus(); } };
    const onPointer = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("pointerdown", onPointer); };
  }, [open]);
  return (
    <nav ref={navRef} className={cx("gd-nav", open && "gd-nav-open", className)} style={style}>
      <a className="gd-mark" href={href}>
        {logo ? <img src={logo} alt={logoAlt} className="gd-logo" /> : <>graspd<i aria-hidden="true">_</i></>}
        {suffix && <small>{suffix}</small>}
      </a>
      {links.length > 0 && (
        <button type="button" ref={toggleRef} className="gd-nav-toggle" aria-expanded={open} aria-controls="gd-nav-links" onClick={() => setOpen((o) => !o)}>
          {open ? "[ close ]" : "[ menu ]"}
        </button>
      )}
      <div className="gd-nav-links" id="gd-nav-links">
        {links.map((l, i) => {
          const o = typeof l === "string" ? { label: l } : l;
          return <a key={i} href={o.href || "#"} className={o.site ? "gd-nav-site" : undefined} aria-current={i === active ? "page" : undefined} onClick={(e) => pick(e, i, o.href)}>{o.label}</a>;
        })}
      </div>
      {cta && <Button arrow href={ctaHref}>{cta}</Button>}
    </nav>
  );
}
